import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { getCurrentUser, isAdmin } from '../utils/userAuth';
import { getRides, addRide, deleteRide, getAllRidesAdmin, adminDeleteListing } from '../utils/listingsStore';
import './Rides.css';

function Rides() {
  const navigate = useNavigate();
  const [rides, setRides] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddForm, setShowAddForm] = useState(false);
  const [activeTab, setActiveTab] = useState('browse');
  const [user, setUser] = useState(null);
  const [filters, setFilters] = useState({
    search: '',
    destination: '',
    sort: 'departureTime'
  });
  const [formData, setFormData] = useState({
    origin: 'SPU Campus',
    destination: '',
    departureDate: '',
    departureTime: '',
    seats: 3,
    price: '',
    description: ''
  });

  useEffect(() => {
    const currentUser = getCurrentUser();
    setUser(currentUser);
    fetchRides();
  }, [filters, activeTab]);

  const fetchRides = () => {
    setLoading(true);
    try {
      let data;
      if (activeTab === 'admin' && user && isAdmin(user.email)) {
        data = getAllRidesAdmin();
      } else if (activeTab === 'my-listings' && user) {
        data = getRides(filters).filter(r => r.driver?.id === user.id);
      } else {
        data = getRides(filters);
      }
      setRides(data);
    } catch (error) {
      console.error('Error fetching rides:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (e) => {
    setFilters({
      ...filters,
      [e.target.name]: e.target.value
    });
  };

  const handleFormChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!user) {
      navigate('/login');
      return;
    }

    try {
      const departureDateTime = new Date(`${formData.departureDate}T${formData.departureTime}`);

      addRide({
        origin: formData.origin,
        destination: formData.destination,
        departureTime: departureDateTime.toISOString(),
        seats: parseInt(formData.seats),
        price: parseFloat(formData.price),
        description: formData.description
      }, user);

      setFormData({
        origin: 'SPU Campus',
        destination: '',
        departureDate: '',
        departureTime: '',
        seats: 3,
        price: '',
        description: ''
      });
      setShowAddForm(false);
      fetchRides();
    } catch (error) {
      console.error('Error adding ride:', error);
    }
  };

  const handleDelete = (rideId) => {
    if (!user) return;

    if (window.confirm('Are you sure you want to delete this ride?')) {
      if (activeTab === 'admin' && isAdmin(user.email)) {
        adminDeleteListing('rides', rideId);
      } else {
        deleteRide(rideId, user.id);
      }
      fetchRides();
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      weekday: 'short',
      month: 'short',
      day: 'numeric'
    });
  };

  const formatTime = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit',
      hour12: true
    });
  };

  const getTimeUntil = (dateString) => {
    const now = new Date();
    const departure = new Date(dateString);
    const diff = departure - now;

    if (diff < 0) return 'Departed';

    const hours = Math.floor(diff / (1000 * 60 * 60));
    const days = Math.floor(hours / 24);

    if (days > 0) return `in ${days}d`;
    if (hours > 0) return `in ${hours}h`;
    return 'Soon';
  };

  // Get minimum date for form (today)
  const getMinDate = () => {
    return new Date().toISOString().split('T')[0];
  };

  return (
    <div className="rides-page">
      {/* Header */}
      <div className="page-header">
        <button className="back-btn" onClick={() => navigate(-1)}>← Back</button>
        <h1 className="page-title">Ride Pool</h1>
        <p className="page-subtitle">Share rides with fellow students</p>
      </div>

      {/* Tabs */}
      <div className="listing-tabs">
        <button
          className={`tab-btn ${activeTab === 'browse' ? 'active' : ''}`}
          onClick={() => setActiveTab('browse')}
        >
          Find Rides
        </button>
        {user && (
          <button
            className={`tab-btn ${activeTab === 'my-listings' ? 'active' : ''}`}
            onClick={() => setActiveTab('my-listings')}
          >
            My Rides
          </button>
        )}
        {user && isAdmin(user.email) && (
          <button
            className={`tab-btn admin-tab ${activeTab === 'admin' ? 'active' : ''}`}
            onClick={() => setActiveTab('admin')}
          >
            Admin
          </button>
        )}
      </div>

      {/* Add Button */}
      {user && (
        <button className="fab-add-btn" onClick={() => setShowAddForm(!showAddForm)}>
          {showAddForm ? '✕' : '+'}
        </button>
      )}

      {/* Quick Add Form */}
      {showAddForm && user && (
        <div className="quick-add-form">
          <h3>Offer a Ride</h3>
          <form onSubmit={handleSubmit}>
            <div className="form-row two-col">
              <input
                type="text"
                name="origin"
                placeholder="Pickup location"
                value={formData.origin}
                onChange={handleFormChange}
                required
              />
              <input
                type="text"
                name="destination"
                placeholder="Destination"
                value={formData.destination}
                onChange={handleFormChange}
                required
              />
            </div>
            <div className="form-row two-col">
              <input
                type="date"
                name="departureDate"
                value={formData.departureDate}
                onChange={handleFormChange}
                required
                min={getMinDate()}
              />
              <input
                type="time"
                name="departureTime"
                value={formData.departureTime}
                onChange={handleFormChange}
                required
              />
            </div>
            <div className="form-row two-col">
              <select name="seats" value={formData.seats} onChange={handleFormChange}>
                <option value="1">1 seat</option>
                <option value="2">2 seats</option>
                <option value="3">3 seats</option>
                <option value="4">4 seats</option>
                <option value="5">5+ seats</option>
              </select>
              <div className="price-input">
                <span className="currency">$</span>
                <input
                  type="number"
                  name="price"
                  placeholder="Price per seat"
                  value={formData.price}
                  onChange={handleFormChange}
                  required
                  min="0"
                  step="0.01"
                />
              </div>
            </div>
            <div className="form-row">
              <textarea
                name="description"
                placeholder="Any additional details (luggage space, stops, etc.)"
                value={formData.description}
                onChange={handleFormChange}
              />
            </div>
            <div className="form-actions">
              <button type="button" className="btn-cancel" onClick={() => setShowAddForm(false)}>
                Cancel
              </button>
              <button type="submit" className="btn-submit">
                Post Ride
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Search & Filters */}
      <div className="rides-search-section">
        <div className="search-container">
          <input
            type="text"
            name="search"
            placeholder="Where are you going?"
            value={filters.search}
            onChange={handleFilterChange}
            className="search-input"
          />
        </div>
        <div className="quick-filters">
          <button
            className={`filter-chip ${filters.destination === '' ? 'active' : ''}`}
            onClick={() => setFilters({...filters, destination: ''})}
          >
            All
          </button>
          <button
            className={`filter-chip ${filters.destination === 'airport' ? 'active' : ''}`}
            onClick={() => setFilters({...filters, destination: 'airport'})}
          >
            Airports
          </button>
          <button
            className={`filter-chip ${filters.destination === 'nyc' ? 'active' : ''}`}
            onClick={() => setFilters({...filters, destination: 'nyc'})}
          >
            NYC
          </button>
          <button
            className={`filter-chip ${filters.destination === 'campus' ? 'active' : ''}`}
            onClick={() => setFilters({...filters, destination: 'campus'})}
          >
            To Campus
          </button>
        </div>
      </div>

      {/* Rides List */}
      <div className="rides-content">
        {loading ? (
          <div className="loading-message">Finding rides...</div>
        ) : rides.length === 0 ? (
          <div className="empty-state">
            <div className="empty-icon">🚗</div>
            <h3>No rides available</h3>
            <p>{activeTab === 'my-listings' ? 'You haven\'t posted any rides yet.' : 'Check back later or post your own ride!'}</p>
            {user && (
              <button className="btn-primary" onClick={() => setShowAddForm(true)}>
                Offer a Ride
              </button>
            )}
          </div>
        ) : (
          <div className="rides-list">
            {rides.map(ride => (
              <div key={ride._id} className="ride-card">
                {/* Time Badge */}
                <div className="ride-time-badge">
                  {getTimeUntil(ride.departureTime)}
                </div>

                {/* Delete Button for owner/admin */}
                {(activeTab === 'my-listings' || activeTab === 'admin') && (
                  <button
                    className="delete-btn ride-delete"
                    onClick={() => handleDelete(ride._id)}
                  >
                    🗑️
                  </button>
                )}

                {/* Route Info */}
                <div className="ride-route">
                  <div className="route-point origin">
                    <div className="point-marker origin-marker"></div>
                    <div className="point-info">
                      <span className="point-label">From</span>
                      <span className="point-name">{ride.origin}</span>
                    </div>
                  </div>

                  <div className="route-line">
                    <div className="route-line-inner"></div>
                  </div>

                  <div className="route-point destination">
                    <div className="point-marker destination-marker"></div>
                    <div className="point-info">
                      <span className="point-label">To</span>
                      <span className="point-name">{ride.destination}</span>
                    </div>
                  </div>
                </div>

                {/* Departure Time */}
                <div className="ride-datetime">
                  <span className="datetime-icon">🕐</span>
                  <span>{formatDate(ride.departureTime)} at {formatTime(ride.departureTime)}</span>
                </div>

                {/* Driver & Details */}
                <div className="ride-details">
                  <div className="driver-info">
                    <div className="driver-avatar">
                      {ride.driver?.name?.charAt(0) || 'D'}
                    </div>
                    <div className="driver-meta">
                      <span className="driver-name">{ride.driver?.name || 'Student Driver'}</span>
                      <span className="seat-count">🪑 {ride.seats} seats</span>
                    </div>
                  </div>

                  <div className="ride-price">
                    <span className="price-amount">${ride.price}</span>
                    <span className="price-label">per seat</span>
                  </div>
                </div>

                {/* Description */}
                {ride.description && (
                  <p className="ride-description">{ride.description}</p>
                )}

                {/* Action Button */}
                <button className="book-ride-btn">
                  Request to Join
                </button>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Login Prompt */}
      {!user && (
        <div className="login-prompt">
          <p>Want to offer a ride?</p>
          <button className="btn-primary" onClick={() => navigate('/login')}>
            Sign in to post
          </button>
        </div>
      )}
    </div>
  );
}

export default Rides;
